import React from "react";
import { Box, Chip, Stack, Typography } from "@mui/material";

/**
 * Ch94 · THE CARD EVERY SOCIALS SECTION SITS IN.
 *
 * 🔴 WHY THIS EXISTS, and it is the whole reason Grant said the build "didn't
 * look complete": the approved mock frames EVERY section as a bordered card
 * with a header row and a status chip. I shipped unframed text on the page
 * background, stretched to whatever width the window had. The behaviour was
 * right and the page read as a skeleton — "1,944 tests green" was never the bar
 * he set, and he rejected three structures before approving this one, so the
 * structure IS the requirement.
 *
 * The `state` chip is the mock's own vocabulary and it is load-bearing for
 * honesty as well as looks: "live today" means the app can genuinely do this
 * now, "to build" means designed but not working yet. A section that cannot do
 * anything must say so ON ITSELF rather than looking identical to one that can.
 */

export type SectionState = "live" | "to_build" | "not_connected";

const STATE_LABEL: Record<SectionState, string> = {
  live: "live today",
  to_build: "to build",
  not_connected: "not set up yet",
};

interface Props {
  title: string;
  state?: SectionState;
  /** Controls that belong in the header row, right-aligned. */
  action?: React.ReactNode;
  children: React.ReactNode;
  /** A tile in a row of tiles rather than a full-width section. */
  dense?: boolean;
  testId?: string;
}

const SocialsCard: React.FC<Props> = ({
  title,
  state,
  action,
  children,
  dense = false,
  testId,
}) => (
  <Box
    data-testid={testId ?? "socials-card"}
    sx={{
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 2,
      p: dense ? 2 : 3,
      bgcolor: "background.paper",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      minWidth: 0,
    }}
  >
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      gap={1}
      sx={{ mb: 2, flexWrap: "wrap" }}
    >
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ lineHeight: 1.4, letterSpacing: 0.8 }}
      >
        {title}
      </Typography>
      <Stack direction="row" alignItems="center" gap={1} sx={{ flexWrap: "wrap", minWidth: 0, maxWidth: "100%" }}>
        {action}
        {state ? (
          <Chip
            size="small"
            variant="outlined"
            label={STATE_LABEL[state]}
            data-testid="card-state"
            sx={{
              height: 20,
              fontSize: 12,
              color: state === "live" ? "success.main" : "text.secondary",
              borderColor: state === "live" ? "success.main" : "divider",
            }}
          />
        ) : null}
      </Stack>
    </Stack>
    <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
  </Box>
);

export default SocialsCard;
